import { useState, useEffect } from 'react'
import { useCanAccess } from '../../hooks/useCanAccess'
import { useSelectionStore } from '../../stores/selectionStore'
import { useBuildingStore } from '../../stores/buildingStore'
import { zoneApi } from '../../services/zoneApi'
import type { Location } from '../../types'
import '../../styles/editors.css'

const ZONE_TYPES = ['TAG_FIXE', 'TAG_MOBILE', 'CAPTEUR', 'PASSERELLE', 'ZONE']

export default function ZoneEditor() {
  const selectedZone = useSelectionStore((s) => s.selectedZone)
  const selectZone = useSelectionStore((s) => s.selectZone)
  const editMode = useSelectionStore((s) => s.editMode)
  const setEditMode = useSelectionStore((s) => s.setEditMode)
  const currentSiteId = useBuildingStore((s) => s.currentSiteId)
  const loadSite = useBuildingStore((s) => s.loadSite)

  const [form, setForm] = useState<Partial<Location>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (selectedZone) {
      setForm({ ...selectedZone })
    } else {
      setForm({})
    }
  }, [selectedZone])

  const updateField = <K extends keyof Location>(key: K, value: Location[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    if (!currentSiteId || !selectedZone) return
    setSaving(true)
    setError(null)
    try {
      await zoneApi.updateLocation(currentSiteId, selectedZone.id, form)
      await loadSite(currentSiteId)
      selectZone(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur sauvegarde')
    }
    setSaving(false)
  }

  const handleDelete = async () => {
    if (!currentSiteId || !selectedZone) return
    setSaving(true)
    setError(null)
    try {
      await zoneApi.deleteLocation(currentSiteId, selectedZone.id)
      selectZone(null)
      await loadSite(currentSiteId)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur suppression')
    }
    setSaving(false)
  }

  const handleCreate = async () => {
    if (!currentSiteId) return
    setSaving(true)
    setError(null)
    try {
      const newZone = await zoneApi.createLocation(currentSiteId, {
        name: 'Nouvelle zone',
        type: 'TAG_FIXE',
        floor: 0,
        x: 0,
        z: 0,
        height: 3,
        cone_angle: 60,
        azimuth_angle: 0,
        tilt_angle: 0,
      })
      await loadSite(currentSiteId)
      selectZone(newZone)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur creation')
    }
    setSaving(false)
  }

  const handleDuplicate = async () => {
    if (!currentSiteId || !selectedZone) return
    setSaving(true)
    setError(null)
    try {
      const { id, site_id, ...rest } = selectedZone
      const newZone = await zoneApi.createLocation(currentSiteId, {
        ...rest,
        name: `${rest.name} (copie)`,
        x: rest.x + 1,
      })
      await loadSite(currentSiteId)
      selectZone(newZone)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur duplication')
    }
    setSaving(false)
  }

  const canCreate = useCanAccess('locations', 'create')
  const canUpdate = useCanAccess('locations', 'update')
  const canDelete = useCanAccess('locations', 'delete')
  const isZoneMode = editMode === 'zone'

  if (!canCreate && !canUpdate) return null

  return (
    <div className="editor-panel">
        <div className="panel-section__title">Editeur de zones</div>

        <div className="editor-panel__toolbar">
          <button
            className={`editor-panel__mode-btn ${isZoneMode ? 'editor-panel__mode-btn--active' : ''}`}
            onClick={() => setEditMode(isZoneMode ? 'none' : 'zone')}
          >
            {isZoneMode ? 'Quitter edition' : 'Mode edition'}
          </button>
          {isZoneMode && canCreate && (
            <button className="editor-panel__create-btn" onClick={handleCreate} disabled={saving}>
              + Nouvelle zone
            </button>
          )}
        </div>

        {selectedZone && (
          <div className="editor-panel__form">
            <div className="editor-panel__field">
              <label>Nom</label>
              <input
                type="text"
                value={form.name ?? ''}
                onChange={(e) => updateField('name', e.target.value)}
              />
            </div>

            <div className="editor-panel__field">
              <label>Libelle</label>
              <input
                type="text"
                value={form.libelle ?? ''}
                onChange={(e) => updateField('libelle', e.target.value)}
              />
            </div>

            <div className="editor-panel__field">
              <label>Type</label>
              <select
                value={form.type ?? 'TAG_FIXE'}
                onChange={(e) => updateField('type', e.target.value)}
              >
                {ZONE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="editor-panel__row">
              <div className="editor-panel__field editor-panel__field--half">
                <label>X</label>
                <input type="number" step="0.1" value={form.x ?? 0} onChange={(e) => updateField('x', parseFloat(e.target.value))} />
              </div>
              <div className="editor-panel__field editor-panel__field--half">
                <label>Z</label>
                <input type="number" step="0.1" value={form.z ?? 0} onChange={(e) => updateField('z', parseFloat(e.target.value))} />
              </div>
            </div>

            <div className="editor-panel__field">
              <label>Hauteur: {form.height?.toFixed(1)}</label>
              <input type="range" min="0.5" max="10" step="0.1" value={form.height ?? 3} onChange={(e) => updateField('height', parseFloat(e.target.value))} />
            </div>

            <div className="editor-panel__field">
              <label>Angle cone: {form.cone_angle}°</label>
              <input type="range" min="10" max="120" step="1" value={form.cone_angle ?? 60} onChange={(e) => updateField('cone_angle', parseInt(e.target.value))} />
            </div>

            <div className="editor-panel__field">
              <label>Azimuth: {form.azimuth_angle}°</label>
              <input type="range" min="0" max="360" step="1" value={form.azimuth_angle ?? 0} onChange={(e) => updateField('azimuth_angle', parseInt(e.target.value))} />
            </div>

            <div className="editor-panel__field">
              <label>Inclinaison: {form.tilt_angle}°</label>
              <input type="range" min="-90" max="90" step="1" value={form.tilt_angle ?? 0} onChange={(e) => updateField('tilt_angle', parseInt(e.target.value))} />
            </div>

            <div className="editor-panel__field">
              <label>Couleur</label>
              <input type="color" value={form.color ?? '#4ade80'} onChange={(e) => updateField('color', e.target.value)} />
            </div>

            <div className="editor-panel__field">
              <label>Etage</label>
              <input type="number" step="1" value={form.floor ?? 0} onChange={(e) => updateField('floor', parseInt(e.target.value))} />
            </div>

            {error && <div className="editor-panel__error">{error}</div>}

            <div className="editor-panel__actions">
              {canUpdate && (
                <button className="editor-panel__btn editor-panel__btn--save" onClick={handleSave} disabled={saving}>
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              )}
              {canCreate && (
                <button className="editor-panel__btn editor-panel__btn--duplicate" onClick={handleDuplicate} disabled={saving}>
                  Dupliquer
                </button>
              )}
              {canDelete && (
                <button className="editor-panel__btn editor-panel__btn--delete" onClick={handleDelete} disabled={saving}>
                  Supprimer
                </button>
              )}
              <button className="editor-panel__btn editor-panel__btn--cancel" onClick={() => selectZone(null)}>
                Annuler
              </button>
            </div>
          </div>
        )}

        {isZoneMode && !selectedZone && (
          <p className="editor-panel__hint">Cliquez sur une zone pour la modifier, ou creez-en une nouvelle.</p>
        )}
      </div>
  )
}
